import Storage from './storage-helper'
import { ContractDataModel, EscolasModel, PessoalDataModel, ResponsavelModel, AlunoModel, UserDataModel } from "@/models/login/login.model";
import { ref } from 'vue';

/**
 * Contador reativo incrementado a cada troca de grupo do usuário.
 *
 * O grupo mora no localStorage, que não é reativo: um `computed` que dependa
 * apenas de `getDescricaoGrupoUsuario()` fica em cache e não reavalia quando o
 * grupo é definido depois da montagem do componente (é o que acontece no login,
 * em que a TopBar define o grupo após o MainTemplate já ter renderizado).
 * Quem precisa reagir à troca de grupo deve ler este ref.
 */
export const versaoGrupoUsuario = ref(0);

/**
 * Contador reativo incrementado sempre que o código de contrato é definido (login).
 *
 * O App.vue (montado uma única vez, na raiz da SPA) precisa saber quando o login
 * termina para só então buscar a configuração de segurança do contrato (timeout de
 * inatividade) — no momento do `onMounted`, se o carregamento inicial cair na tela de
 * login, ainda não há contrato algum. Sem este contador, a aplicação da configuração
 * customizada dependia de um reload completo da página depois do login.
 */
export const versaoCodContrato = ref(0);

enum Locals {
  ESCOLA_ATUAL = 'escolaAtual',
  CODIGO_ESCOLA_ATUAL = 'codigoEscolaAtual',
  NOME_ESCOLA_ATUAL = 'nomeEscolaAtual',
  CONTRACT_DATA = 'contractData',
  ANO_ATUAL = 'anoAtual',
  SEMESTRE_ATUAL = 'semestreAtual',
  USER_DATA = 'userData',
  PESSOAL_DATA = 'pessoalData',
  RESPONSAVEL_DATA = 'responsavelData',
  ALUNO_DATA = 'alunoData',
  ACCESS_TOKEN_USER = 'accessToken',
  COD_CONTRATO = 'codcontrato',
  COD_GRUPO_USUARIO = 'codgrupousuario',
  COD_MODULO_SISTEMA = 'codmodulosistema',
  DESC_GRUPO_USUARIO = 'grupousuario',
  EXIBIU_MURAL_AVISOS = 'exibiuMuralAvisos',
  LOGIN_COM_CONTRATO_HOMOLOGACAO = 'logadoContratoHomologacao',
  PROXIMO_NO_SAEWEB_LEGACY_PARA_USAR = 'proximoNoSaeWebLegacyUsar',
  SESSION_ID = 'sessionId'
}

export default class DadosLocais extends Storage<Locals> {
  private static instance?: DadosLocais

  private constructor() {
    super()
  }

  public static getInstance() {
    if (!this.instance)
      this.instance = new DadosLocais()

    return this.instance
  }

  public setLoginContratoHomologacao(loginHomologacao: boolean) {
    this.set(Locals.LOGIN_COM_CONTRATO_HOMOLOGACAO, (loginHomologacao) ? "1" : "0")
  }

  public getLoginContratoHomologacao(): boolean {
    const result = this.get(Locals.LOGIN_COM_CONTRATO_HOMOLOGACAO);
    return result == "1";
  }

  public setExibiuMuralAvisos(exibiu: boolean) {
    this.set(Locals.EXIBIU_MURAL_AVISOS, (exibiu) ? "1" : "0")
  }

  public getExibiuMuralAvisos(): boolean {
    const result = this.get(Locals.EXIBIU_MURAL_AVISOS);
    return result == "1";
  }

  public getUserData(): UserDataModel {
    const userData = this.get(Locals.USER_DATA);
    return userData ? JSON.parse(userData) : new UserDataModel();
  }

  public setUserData(userData: UserDataModel) {
    this.set(Locals.USER_DATA, JSON.stringify(userData))
  }

  /**
   * Item 48.1 (auditoria de sessão): identificador da sessão devolvido pelo login
   * (Users/AppAuthenticate) — não é o token em si (esse fica só no cookie HttpOnly), só um
   * GUID de correlação, guardado pra mandar de volta no Logoff.
   */
  public setSessionId(sessionId: string | null | undefined) {
    this.set(Locals.SESSION_ID, sessionId ?? '')
  }

  public getSessionId(): string | null {
    return this.get(Locals.SESSION_ID) || null;
  }

  public getContractData(): ContractDataModel {
    const contractData = this.get(Locals.CONTRACT_DATA);
    return contractData ? JSON.parse(contractData) : new ContractDataModel();
  }

  public setContractData(contractData: ContractDataModel) {
    this.set(Locals.CONTRACT_DATA, JSON.stringify(contractData))
  }

  public getPessoalData(): PessoalDataModel | null {
    const pessoalData = this.get(Locals.PESSOAL_DATA);

    if (pessoalData && typeof pessoalData === "string") {
      try {
        return JSON.parse(pessoalData);
      } catch (error) {
        //console.error("Erro ao parsear JSON:", error);
        return null;
      }
    }
    return null;
  }

  public setPessoalData(pessoalData: PessoalDataModel) {
    this.set(Locals.PESSOAL_DATA, JSON.stringify(pessoalData))
  }

  public getResponsavelData(): ResponsavelModel | null {
    const responsavelData = this.get(Locals.RESPONSAVEL_DATA);

    if (responsavelData && typeof responsavelData === "string") {
      try {
        return JSON.parse(responsavelData);
      } catch (error) {
        //console.error("Erro ao parsear JSON:", error);
        return null;
      }
    }
    return null;
  }

  public setResponsavelData(responsavelData: ResponsavelModel) {
    this.set(Locals.RESPONSAVEL_DATA, JSON.stringify(responsavelData))
  }

  public getAlunoData(): AlunoModel | null {
    const alunoData = this.get(Locals.ALUNO_DATA);

    if (alunoData && typeof alunoData === "string") {
      try {
        return JSON.parse(alunoData);
      } catch (error) {
        //console.error("Erro ao parsear JSON:", error);
        return null;
      }
    }
    return null;
  }

  public setAlunoData(alunoData: AlunoModel) {
    this.set(Locals.ALUNO_DATA, JSON.stringify(alunoData))
  }

  public setCodContrato(codContrato: string) {
    this.set(Locals.COD_CONTRATO, codContrato)
    versaoCodContrato.value += 1
  }

  public getCodContrato() {
    return this.get(Locals.COD_CONTRATO)
  }

  public getAccessToken() {
    return this.get(Locals.ACCESS_TOKEN_USER)
  }

  public setAccessToken(accessToken?: string | null) {
    // Fase 4 (cookie seguro): em produção o corpo da resposta não traz accessToken nenhum — a
    // propriedade nem existe no JSON, então aqui chega undefined (não null). Sem esta guarda,
    // localStorage.setItem grava a STRING literal "undefined" (coerção do navegador), que é um
    // valor truthy e não-vazio — poderia ser lido como "token presente" em qualquer checagem
    // ingênua. Só grava de verdade nos cenários de exceção (localhost) onde o token real existe;
    // caso contrário, garante que a chave fique limpa.
    if (accessToken) {
      this.set(Locals.ACCESS_TOKEN_USER, accessToken)
    } else {
      this.clearItem(Locals.ACCESS_TOKEN_USER)
    }
  }

  public getNomeEscolaSelecionada() {
    return this.get(Locals.NOME_ESCOLA_ATUAL)
  }

  public setNomeEscolaSelecionada(nomeEscolaSelecionada: string) {
    this.set(Locals.NOME_ESCOLA_ATUAL, nomeEscolaSelecionada)
  }

  public getCodigoEscolaSelecionada() {
    return this.get(Locals.CODIGO_ESCOLA_ATUAL)
  }

  public setEscolaSelecionadaData(escolaData: EscolasModel) {
    this.set(Locals.ESCOLA_ATUAL, JSON.stringify(escolaData))
  }

  public getModuloSelecionado() {
    return this.get(Locals.COD_MODULO_SISTEMA)
  }

  public getEscolaSelecionadaData(): EscolasModel {
    const escolaData = this.get(Locals.ESCOLA_ATUAL);
    return escolaData ? JSON.parse(escolaData) : new EscolasModel("", 0);
  }

  public setCodigoEscolaSelecionada(codigoEscola: string) {
    this.set(Locals.CODIGO_ESCOLA_ATUAL, codigoEscola)
  }

  public getCodigoGrupoUsuario() {
    return this.get(Locals.COD_GRUPO_USUARIO)
  }

  public setCodigoGrupoUsuario(codigoGrupoUsuario: string) {
    this.set(Locals.COD_GRUPO_USUARIO, codigoGrupoUsuario)
    versaoGrupoUsuario.value += 1
  }

  public getCodigoModuloSelecionado() {
    return this.get(Locals.COD_MODULO_SISTEMA)
  }

  public setCodigoModuloSelecionado(codigoModulo: string) {
    this.set(Locals.COD_MODULO_SISTEMA, codigoModulo)
  }

  public setDescricaoGrupoUsuario(descGrupoUsuario: string) {
    this.set(Locals.DESC_GRUPO_USUARIO, descGrupoUsuario)
    versaoGrupoUsuario.value += 1
  }

  public getDescricaoGrupoUsuario() {
    return this.get(Locals.DESC_GRUPO_USUARIO) ?? ""
  }

  public getAnoSelecionado() {
    return this.get(Locals.ANO_ATUAL)
  }

  public setAnoSelecionado(anoSelecionado: string) {
    this.set(Locals.ANO_ATUAL, anoSelecionado)
  }

  public getSemestreSelecionado() {
    return this.get(Locals.SEMESTRE_ATUAL)
  }

  public setSemestreSelecionado(semestreSelecionado: string) {
    this.set(Locals.SEMESTRE_ATUAL, semestreSelecionado)
  }

  public getProximoNoSaeWebLegacyUsar() {
    let proximoNo = this.get(Locals.PROXIMO_NO_SAEWEB_LEGACY_PARA_USAR);

    if (proximoNo)
      proximoNo = (proximoNo === "9") ? "0" : (parseInt(proximoNo) + 1).toString()
    else
      proximoNo = "0"

    this.set(Locals.PROXIMO_NO_SAEWEB_LEGACY_PARA_USAR, proximoNo)
    return proximoNo
  }

  public logout() {
    // Favoritos (menu lateral e TopBar) e preferências de grid (filtros/ordenação/larguras de
    // coluna) são preferências do usuário no dispositivo, não dados de sessão — precisam
    // sobreviver ao logoff. Varre por prefixo em vez de montar `favoritos_${id}` com o usuário
    // atual porque este método também é chamado no INÍCIO do login (Login.vue.entrar(), antes
    // de autenticar) — nesse momento USER_DATA já foi zerado por um logoff anterior, então não
    // dá pra saber de antemão qual é o id do usuário cujos favoritos preservar.
    const chavesPreservadas: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const chave = localStorage.key(i);
      if (chave && (chave === 'menu_favoritos' || chave === 'grids' || chave.startsWith('favoritos_'))) {
        chavesPreservadas[chave] = localStorage.getItem(chave) ?? '';
      }
    }

    this.clearAll();
    sessionStorage.clear();
    localStorage.clear();

    Object.entries(chavesPreservadas).forEach(([chave, valor]) => localStorage.setItem(chave, valor));

    // Notifica quem observa versaoCodContrato (ex: App.vue) que a sessão terminou — sem isto, um
    // timer de aviso de inatividade já agendado antes do logoff continuaria pendente e apareceria
    // na tela de login, sem sessão nenhuma.
    versaoCodContrato.value += 1;
  }

  public clearAll() {
    this.clearItems([
      Locals.ESCOLA_ATUAL,
      Locals.CODIGO_ESCOLA_ATUAL,
      Locals.NOME_ESCOLA_ATUAL,
      Locals.CONTRACT_DATA,
      Locals.ANO_ATUAL,
      Locals.SEMESTRE_ATUAL,
      Locals.USER_DATA,
      Locals.PESSOAL_DATA,
      Locals.RESPONSAVEL_DATA,
      Locals.ALUNO_DATA,
      Locals.ACCESS_TOKEN_USER,
      Locals.COD_CONTRATO,
      Locals.COD_GRUPO_USUARIO,
      Locals.COD_MODULO_SISTEMA,
      Locals.DESC_GRUPO_USUARIO,
      Locals.EXIBIU_MURAL_AVISOS,
      Locals.LOGIN_COM_CONTRATO_HOMOLOGACAO
    ])
  }
}
